import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { Markdown } from '@/lib/markdown';
import { useAuthStore } from '@/store/auth';
import {
  listSpaces, createSpace, updateSpace, deleteSpace, reorderSpaces,
  listPages, getPage, createPage, updatePage, deletePage, reorderPages,
  listShareTokens, createShareToken, revokeShareToken,
  type WikiSpace, type WikiPageRef,
} from '@/api/wiki';

function shareUrl(token: string) {
  return `${window.location.origin}/wiki/s/${token}`;
}

function formatDate(unix: number) {
  return new Date(unix * 1000).toLocaleString();
}

export function Wiki() {
  const qc = useQueryClient();
  const hasSection = useAuthStore((s) => s.hasSection);
  // super_admin gets every section from the store, so these cover it too.
  const canEdit = hasSection('wiki-edit');
  const canShare = hasSection('wiki-share');

  const { data: spaces = [], isLoading } = useQuery({ queryKey: ['wiki-spaces'], queryFn: listSpaces });

  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [pageId, setPageId] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  // Draft lives outside react-query so typing never fights a refetch.
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [dirty, setDirty] = useState(false);

  const activeSpace = useMemo(
    () => spaces.find((s) => s.id === spaceId) ?? null,
    [spaces, spaceId],
  );

  // Default to the first space, and drop a selection whose space is gone.
  useEffect(() => {
    if (spaces.length === 0) { setSpaceId(null); return; }
    if (!spaceId || !spaces.some((s) => s.id === spaceId)) setSpaceId(spaces[0]!.id);
  }, [spaces, spaceId]);

  const { data: pages = [] } = useQuery({
    queryKey: ['wiki-pages', spaceId],
    queryFn: () => listPages(spaceId!),
    enabled: !!spaceId,
  });

  useEffect(() => {
    if (pages.length === 0) { setPageId(null); return; }
    if (!pageId || !pages.some((p) => p.id === pageId)) setPageId(pages[0]!.id);
  }, [pages, pageId]);

  const { data: page } = useQuery({
    queryKey: ['wiki-page', pageId],
    queryFn: () => getPage(pageId!),
    enabled: !!pageId,
  });

  // Load the fetched page into the draft, but never clobber unsaved edits.
  useEffect(() => {
    if (!page) { setDraftTitle(''); setDraftBody(''); setDirty(false); return; }
    if (dirty) return;
    setDraftTitle(page.title);
    setDraftBody(page.body_md);
  }, [page, dirty]);

  const invalidateSpaces = () => qc.invalidateQueries({ queryKey: ['wiki-spaces'] });
  const invalidatePages = () => qc.invalidateQueries({ queryKey: ['wiki-pages', spaceId] });

  const addSpace = useMutation({
    mutationFn: (name: string) => createSpace({ name }),
    onSuccess: (s) => { setSpaceId(s.id); invalidateSpaces(); },
  });
  const renameSpace = useMutation({
    mutationFn: (v: { id: string; name: string }) => updateSpace(v.id, { name: v.name }),
    onSuccess: invalidateSpaces,
  });
  const removeSpace = useMutation({
    mutationFn: deleteSpace,
    onSuccess: () => { setSpaceId(null); setPageId(null); invalidateSpaces(); },
  });
  const moveSpaces = useMutation({ mutationFn: reorderSpaces, onSuccess: invalidateSpaces });

  const addPage = useMutation({
    mutationFn: (title: string) => createPage(spaceId!, { title }),
    onSuccess: (p) => { setDirty(false); setPageId(p.id); invalidatePages(); invalidateSpaces(); },
  });
  const savePage = useMutation({
    mutationFn: () => updatePage(pageId!, { title: draftTitle, body_md: draftBody }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['wiki-page', pageId] });
      invalidatePages();
    },
  });
  const removePage = useMutation({
    mutationFn: deletePage,
    onSuccess: () => { setDirty(false); setPageId(null); invalidatePages(); invalidateSpaces(); },
  });
  const movePages = useMutation({
    mutationFn: (ids: string[]) => reorderPages(spaceId!, ids),
    onSuccess: invalidatePages,
  });

  function moveSpace(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= spaces.length) return;
    const next = spaces.slice();
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    moveSpaces.mutate(next.map((s) => s.id));
  }

  function movePage(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= pages.length) return;
    const next = pages.slice();
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    movePages.mutate(next.map((p) => p.id));
  }

  function selectPage(id: string) {
    if (dirty && !confirm('Несохранённые изменения будут потеряны. Продолжить?')) return;
    setDirty(false);
    setPageId(id);
    setPreview(false);
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-content">Wiki</h1>
          <p className="mt-1 text-sm text-content-muted">
            Документация по проекту. Пространство можно отдать наружу по ссылке — без входа в панель.
          </p>
        </div>
      </div>

      {isLoading && <div className="text-sm text-content-subtle">Загрузка…</div>}

      {!isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-6 items-start">
          <SpaceList
            spaces={spaces}
            activeId={spaceId}
            canEdit={canEdit}
            onSelect={(id) => { setSpaceId(id); setDirty(false); setPreview(false); }}
            onAdd={(name) => addSpace.mutate(name)}
            onRename={(id, name) => renameSpace.mutate({ id, name })}
            onDelete={(s) => {
              if (confirm(`Удалить пространство «${s.name}» со всеми статьями и ссылками?`)) {
                removeSpace.mutate(s.id);
              }
            }}
            onMove={moveSpace}
          />

          <div className="min-w-0 space-y-6">
            {!activeSpace && (
              <div className="bg-surface border border-border rounded-lg px-4 py-8 text-sm text-content-subtle">
                {canEdit ? 'Создайте первое пространство слева.' : 'Пространств пока нет.'}
              </div>
            )}

            {activeSpace && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-[14rem_1fr] gap-4 items-start">
                  <PageList
                    pages={pages}
                    activeId={pageId}
                    canEdit={canEdit}
                    onSelect={selectPage}
                    onAdd={(title) => addPage.mutate(title)}
                    onMove={movePage}
                  />

                  <div className="min-w-0 bg-surface border border-border rounded-lg">
                    {!pageId && (
                      <div className="px-4 py-8 text-sm text-content-subtle">
                        {canEdit ? 'Добавьте статью слева.' : 'В этом пространстве пока нет статей.'}
                      </div>
                    )}

                    {pageId && (
                      <>
                        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                          <input
                            value={draftTitle}
                            onChange={(e) => { setDraftTitle(e.target.value); setDirty(true); }}
                            readOnly={!canEdit}
                            placeholder="Заголовок статьи"
                            className="flex-1 min-w-0 bg-transparent text-content font-medium outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setPreview((p) => !p)}
                            className="text-xs px-2 py-1 rounded border border-border text-content-muted hover:text-content"
                          >
                            {preview ? 'Редактор' : 'Превью'}
                          </button>
                          <EditOnly>
                            {canEdit && (
                              <>
                                <Button
                                  onClick={() => savePage.mutate()}
                                  disabled={!dirty || savePage.isPending}
                                >
                                  {savePage.isPending ? 'Сохраняю…' : 'Сохранить'}
                                </Button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (confirm('Удалить статью?')) removePage.mutate(pageId);
                                  }}
                                  className="text-xs px-2 py-1 rounded border border-border text-danger hover:bg-surface-muted"
                                >
                                  Удалить
                                </button>
                              </>
                            )}
                          </EditOnly>
                        </div>

                        {preview || !canEdit ? (
                          <div className="px-4 py-3">
                            {draftBody.trim()
                              ? <Markdown source={draftBody} />
                              : <p className="text-sm text-content-subtle">Пусто.</p>}
                          </div>
                        ) : (
                          <textarea
                            value={draftBody}
                            onChange={(e) => { setDraftBody(e.target.value); setDirty(true); }}
                            placeholder="Markdown…"
                            spellCheck={false}
                            className="w-full h-[28rem] px-4 py-3 bg-transparent text-content font-mono text-sm outline-none resize-y"
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>

                {canShare && <SharePanel space={activeSpace} />}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- sub-views

function SpaceList({ spaces, activeId, canEdit, onSelect, onAdd, onRename, onDelete, onMove }: {
  spaces: WikiSpace[];
  activeId: string | null;
  canEdit: boolean;
  onSelect: (id: string) => void;
  onAdd: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (space: WikiSpace) => void;
  onMove: (idx: number, dir: -1 | 1) => void;
}) {
  const [name, setName] = useState('');

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-wide text-content-subtle font-semibold">
        Пространства
      </div>
      <ul className="divide-y divide-border">
        {spaces.map((s, idx) => (
          <li key={s.id} className={`group flex items-center gap-1 px-2 py-2 ${s.id === activeId ? 'bg-surface-muted' : ''}`}>
            <button
              type="button"
              onClick={() => onSelect(s.id)}
              className="flex-1 min-w-0 text-left px-2 text-sm text-content truncate"
              title={s.name}
            >
              {s.name}
              <span className="ml-2 text-xs text-content-subtle">{s.page_count}</span>
            </button>
            {canEdit && (
              <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <IconBtn label="Вверх" onClick={() => onMove(idx, -1)}>↑</IconBtn>
                <IconBtn label="Вниз" onClick={() => onMove(idx, 1)}>↓</IconBtn>
                <IconBtn
                  label="Переименовать"
                  onClick={() => {
                    const next = prompt('Название пространства', s.name)?.trim();
                    if (next && next !== s.name) onRename(s.id, next);
                  }}
                >
                  ✎
                </IconBtn>
                <IconBtn label="Удалить" onClick={() => onDelete(s)}>×</IconBtn>
              </span>
            )}
          </li>
        ))}
        {spaces.length === 0 && (
          <li className="px-4 py-3 text-sm text-content-subtle">Пока пусто.</li>
        )}
      </ul>
      <EditOnly>
        {canEdit && (
          <form
            className="p-2 border-t border-border flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = name.trim();
              if (!trimmed) return;
              onAdd(trimmed);
              setName('');
            }}
          >
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Новое пространство"
              className="flex-1 min-w-0 px-2 py-1 text-sm bg-surface-sunken border border-border rounded text-content"
            />
            <Button type="submit">+</Button>
          </form>
        )}
      </EditOnly>
    </div>
  );
}

function PageList({ pages, activeId, canEdit, onSelect, onAdd, onMove }: {
  pages: WikiPageRef[];
  activeId: string | null;
  canEdit: boolean;
  onSelect: (id: string) => void;
  onAdd: (title: string) => void;
  onMove: (idx: number, dir: -1 | 1) => void;
}) {
  const [title, setTitle] = useState('');

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-wide text-content-subtle font-semibold">
        Статьи
      </div>
      <ul className="divide-y divide-border">
        {pages.map((p, idx) => (
          <li key={p.id} className={`group flex items-center gap-1 px-2 py-2 ${p.id === activeId ? 'bg-surface-muted' : ''}`}>
            <button
              type="button"
              onClick={() => onSelect(p.id)}
              className="flex-1 min-w-0 text-left px-2 text-sm text-content truncate"
              title={p.title}
            >
              {p.title}
            </button>
            {canEdit && (
              <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <IconBtn label="Вверх" onClick={() => onMove(idx, -1)}>↑</IconBtn>
                <IconBtn label="Вниз" onClick={() => onMove(idx, 1)}>↓</IconBtn>
              </span>
            )}
          </li>
        ))}
        {pages.length === 0 && (
          <li className="px-4 py-3 text-sm text-content-subtle">Пока пусто.</li>
        )}
      </ul>
      <EditOnly>
        {canEdit && (
          <form
            className="p-2 border-t border-border flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = title.trim();
              if (!trimmed) return;
              onAdd(trimmed);
              setTitle('');
            }}
          >
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Новая статья"
              className="flex-1 min-w-0 px-2 py-1 text-sm bg-surface-sunken border border-border rounded text-content"
            />
            <Button type="submit">+</Button>
          </form>
        )}
      </EditOnly>
    </div>
  );
}

function SharePanel({ space }: { space: WikiSpace }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const { data: tokens = [] } = useQuery({
    queryKey: ['wiki-tokens', space.id],
    queryFn: () => listShareTokens(space.id),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['wiki-tokens', space.id] });
  const mint = useMutation({
    mutationFn: () => createShareToken(space.id, label.trim() || undefined),
    onSuccess: () => { setLabel(''); invalidate(); },
  });
  const revoke = useMutation({ mutationFn: revokeShareToken, onSuccess: invalidate });

  async function copy(token: string) {
    await navigator.clipboard.writeText(shareUrl(token));
    setCopied(token);
    setTimeout(() => setCopied(null), 1500);
  }

  const active = tokens.filter((t) => !t.revoked_at);
  const revoked = tokens.filter((t) => t.revoked_at);

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="text-sm font-medium text-content">
          Публичные ссылки на «{space.name}»
        </div>
        <div className="text-xs text-content-subtle">
          Открывают всё пространство на чтение, без входа
        </div>
      </div>

      <EditOnly>
        <form
          className="p-3 border-b border-border flex gap-2"
          onSubmit={(e) => { e.preventDefault(); mint.mutate(); }}
        >
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Кому выдаём — подрядчику по крео, новому ASO…"
            className="flex-1 min-w-0 px-2 py-1 text-sm bg-surface-sunken border border-border rounded text-content"
          />
          <Button type="submit" disabled={mint.isPending}>
            {mint.isPending ? 'Создаю…' : 'Создать ссылку'}
          </Button>
        </form>
      </EditOnly>

      <ul className="divide-y divide-border">
        {active.map((t) => (
          <li key={t.token} className="px-4 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-content truncate">{t.label || 'Без подписи'}</div>
              <div className="text-xs text-content-subtle font-mono truncate">{shareUrl(t.token)}</div>
            </div>
            <span className="text-xs text-content-subtle whitespace-nowrap">{formatDate(t.created_at)}</span>
            <button
              type="button"
              onClick={() => copy(t.token)}
              className="text-xs px-2 py-1 rounded border border-border text-content-muted hover:text-content"
            >
              {copied === t.token ? 'Скопировано' : 'Копировать'}
            </button>
            <EditOnly>
              <button
                type="button"
                onClick={() => {
                  if (confirm('Отозвать ссылку? Она перестанет открываться сразу.')) revoke.mutate(t.token);
                }}
                className="text-xs px-2 py-1 rounded border border-border text-danger hover:bg-surface-muted"
              >
                Отозвать
              </button>
            </EditOnly>
          </li>
        ))}
        {active.length === 0 && (
          <li className="px-4 py-3 text-sm text-content-subtle">Активных ссылок нет.</li>
        )}
        {revoked.map((t) => (
          <li key={t.token} className="px-4 py-2 flex items-center gap-3 text-content-subtle">
            <span className="flex-1 min-w-0 text-xs truncate line-through">
              {t.label || 'Без подписи'}
            </span>
            <span className="text-xs whitespace-nowrap">отозвана {formatDate(t.revoked_at!)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function IconBtn({ label, onClick, children }: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="w-6 h-6 flex items-center justify-center text-content-subtle hover:text-content rounded"
    >
      {children}
    </button>
  );
}
